import { useState } from 'react';
import { AlertTriangle, ChevronRight, RotateCcw } from 'lucide-react';
import type { ChoiceGroup, ChoiceOption } from '../hooks/useSoteriaChat';
import { cn } from '@/lib/utils';

/**
 * ChoiceCard
 * ----------
 * Clickable buttons for the strategy-builder's "choose one" clarifications
 * (base universe, reconstitution frequency). The backend sends one group per
 * field it is actually asking about — only fields with a discrete option list,
 * so this mirrors the text question exactly and never invents a menu.
 *
 * ONE TURN FOR ALL OF THEM. The backend sends every choice field at once and
 * this is a small form over the lot: answers live in local state, and nothing
 * reaches the server until Confirm.
 *
 * That is the whole point of the local state. Sending on each click cost a
 * full server turn per field (critic + requirements validation, several LLM
 * calls each), so picking a universe and a reconstitution frequency meant two
 * round trips and, in the worst case, a strategy build wedged between them.
 *
 * CONFIRM IS ALWAYS REQUIRED, and that is deliberate. An earlier version sent
 * as soon as the last question was answered, which meant an advisor exploring
 * the options — clicking one to see what it said, then changing their mind —
 * could fire a turn they never meant to send. It also made the answer that
 * triggered a follow-up unchangeable, since answering the follow-up completed
 * the set. One button, pressed on purpose, removes both.
 *
 * CLASHING OPTIONS ARE MARKED, NOT REMOVED. A rebalance has to be more
 * frequent than a reconstitution, so "rebalances quarterly" rules out
 * quarterly reconstitution. Hiding that option would hide the contradiction:
 * an advisor who says "rebalances quarterly" has very often described ONE
 * schedule in loose words and means reconstitution. So the option stays
 * clickable, and picking it reveals a follow-up group (`shown_when`) that asks
 * what the rebalance should do instead — including turning it off. Both are
 * answered before anything is sent, so resolving the clash costs no extra turn.
 *
 * Previously the server resolved this silently: quarterly rebalancing plus
 * quarterly reconstitution became MONTHLY rebalancing, a cadence the advisor
 * never chose and was never told about.
 *
 * Answers are labelled with their question on submit, so the agent gets
 * `<question>: <value>` per line rather than bare values it has to attribute by
 * guesswork. Typing a free-text answer still works, so nothing is lost for
 * keyboard/AT users — and free-text fields (no option list) never reach here;
 * they stay in the prose question.
 *
 * Rendered only on the latest turn (see SoteriaMessageList): once the set is
 * submitted — or a new turn arrives — the buttons disappear and the answers
 * remain as the sent message, so the transcript never accumulates dead button
 * rows.
 */
export function ChoiceCard({
  groups,
  onChoose,
}: {
  groups: Array<ChoiceGroup>;
  onChoose: (value: string) => void;
}) {
  // Keyed by the group's INDEX, not by its field.
  //
  // Follow-up groups deliberately share a field name — only one of them can be
  // shown, since only one triggering answer can be chosen — but keying state by
  // field made answering one count as answering ALL of them. On submit that
  // emitted a line per group, each printing its own question with the single
  // answer pasted underneath, so the agent received a rebalance decision it was
  // never given against a reconstitution the advisor never picked.
  const [answers, setAnswers] = useState<Record<number, string>>({});
  // Set once the turn has been sent, so the card stops accepting clicks while
  // the reply is on its way. Without it a second Confirm sends a second turn.
  const [sent, setSent] = useState(false);

  if (!groups.length || sent) return null;

  const answerFor = (field: string): string | undefined => {
    const i = groups.findIndex((g) => g.field === field && !g.shown_when);
    return i === -1 ? undefined : answers[i];
  };

  // A follow-up is asked once an earlier answer triggers it — and STAYS on
  // screen once answered, even if the trigger no longer holds. Letting it
  // vanish while its answer still rides along is how an advisor ends up
  // sending a rebalance they can no longer see: they resolved a clash, changed
  // their mind about what caused it, and the resolution quietly stayed.
  // Whether the clash that opens a follow-up currently holds.
  const isTriggered = (g: ChoiceGroup): boolean =>
    !!g.shown_when &&
    g.shown_when.values.includes(answerFor(g.shown_when.field) ?? '');

  const isVisible = (g: ChoiceGroup, i: number): boolean =>
    !g.shown_when || isTriggered(g) || i in answers;

  const visible = groups
    .map((g, i) => ({ group: g, index: i }))
    .filter(({ group, index }) => isVisible(group, index));
  const ready = visible.every(({ index }) => index in answers);

  const pick = (index: number, value: string) => {
    const next: Record<number, string> = { ...answers, [index]: value };
    const picked = groups[index];
    // Changing a backbone answer can SUPERSEDE a follow-up already answered:
    // pick a clashing option, resolve it, then pick the OTHER clashing option
    // and a second follow-up opens on the same field. Both would then be
    // answered and both would be sent. Only the one this value actually
    // triggers survives.
    //
    // Note the guard: nothing is cleared when the new value triggers NO
    // follow-up. That is the advisor moving to a clean option, where their
    // rebalance decision stands — which is the persistence rule.
    if (!picked.shown_when) {
      const triggeredNow = groups.findIndex(
        (g) => g.shown_when?.field === picked.field &&
          g.shown_when.values.includes(value),
      );
      if (triggeredNow !== -1) {
        groups.forEach((g, i) => {
          if (i !== triggeredNow && g.shown_when?.field === picked.field) {
            delete next[i];
          }
        });
      }
    }
    setAnswers(next);
  };

  const submit = () => {
    // One line per ANSWERED group, in the order they were asked. Indexing by
    // group is what keeps that one-to-one: a shared field name no longer
    // duplicates the answer across every group that carries it.
    const lines = groups
      .map((g, i) => (i in answers ? `${g.question}: ${answers[i]}` : null))
      .filter((line): line is string => line !== null);
    setSent(true);
    onChoose(lines.join('\n'));
  };

  return (
    <div className="mt-3 space-y-4">
      {visible.map(({ group, index }) => (
        <div key={index}>
          <ChoiceGroupBlock
            group={group}
            selected={answers[index]}
            onPick={(value) => pick(index, value)}
            tone={group.shown_when ? 'warning' : 'muted'}
          />
          {/* Only once the clash that opened this group is gone. Until then the
              answer is required, and taking it away would just reopen the
              question the advisor is in the middle of answering. */}
          {group.shown_when && group.revert_to && !isTriggered(group) ? (
            // Full-width and bordered like an option, because it IS one: the
            // clash is gone, so keeping the original rebalance is now a real
            // choice rather than a footnote. As a small underlined link it was
            // missable, and missing it leaves the advisor holding a workaround
            // for a problem they have already solved.
            <button
              type="button"
              onClick={() => {
                const next = { ...answers };
                delete next[index];
                setAnswers(next);
              }}
              className="mt-2 flex w-full items-center gap-2.5 rounded-lg border border-primary/40 bg-primary/5 p-3 text-left transition-colors hover:border-primary/70 hover:bg-primary/10"
            >
              <RotateCcw className="h-4 w-4 shrink-0 text-primary" />
              <span className="min-w-0">
                <span className="block text-sm font-semibold text-foreground">
                  Keep {group.revert_to.toLowerCase()} rebalancing, as you asked
                </span>
                <span className="mt-0.5 block text-xs text-muted-foreground">
                  There is no longer a clash, so your original request can
                  stand. This undoes the change above.
                </span>
              </span>
            </button>
          ) : null}
        </div>
      ))}
      <button
        type="button"
        disabled={!ready}
        onClick={submit}
        className={cn(
          'w-full rounded-lg px-3 py-2 text-sm font-semibold transition-colors',
          ready
            ? 'bg-foreground text-background hover:bg-foreground/90'
            : 'cursor-not-allowed bg-muted text-muted-foreground',
        )}
      >
        {ready ? 'Confirm' : 'Choose an option for each question'}
      </button>
    </div>
  );
}

function ChoiceGroupBlock({
  group,
  selected,
  onPick,
  hideQuestion = false,
  tone = 'muted',
}: {
  group: ChoiceGroup;
  selected?: string;
  onPick: (value: string) => void;
  hideQuestion?: boolean;
  tone?: 'muted' | 'warning';
}) {
  return (
    <div>
      {hideQuestion ? null : (
        <p
          className={cn(
            'mb-2 text-xs font-medium uppercase tracking-wide',
            tone === 'warning' ? 'text-warning' : 'text-muted-foreground',
          )}
        >
          {group.question}
        </p>
      )}
      <div className="flex flex-col gap-1.5">
        {group.options.map((opt) => (
          <OptionButton
            key={opt.value}
            option={opt}
            isSelected={selected === opt.value}
            onPick={() => onPick(opt.value)}
          />
        ))}
      </div>
    </div>
  );
}

function OptionButton({
  option,
  isSelected,
  onPick,
}: {
  option: ChoiceOption;
  isSelected: boolean;
  onPick: () => void;
}) {
  // `allowed` is absent on older payloads and on every field with no
  // constraint, so only an explicit `false` marks an option.
  const clashes = option.allowed === false;
  return (
    <button
      type="button"
      onClick={onPick}
      // Deliberately NOT `disabled`. The option is reachable on purpose — see
      // the note on ChoiceCard — so it must stay focusable and announce its
      // state rather than being removed from the tab order.
      aria-pressed={isSelected}
      className={cn(
        'group flex items-center justify-between gap-3 rounded-lg border p-3 text-left transition-all hover:shadow-sm',
        isSelected
          ? 'border-primary bg-accent'
          : 'border-border bg-card hover:border-primary/60 hover:bg-accent',
        clashes && !isSelected && 'border-dashed border-warning/50 bg-warning/5',
      )}
    >
      <span className="min-w-0">
        <span
          className={cn(
            'flex items-center gap-1.5 text-sm font-semibold',
            clashes ? 'text-warning' : 'text-foreground',
          )}
        >
          {clashes ? (
            <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          ) : null}
          {option.label}
        </span>
        {option.description ? (
          <span className="mt-0.5 block text-xs text-muted-foreground">
            {option.description}
          </span>
        ) : null}
        {clashes && option.conflict ? (
          // The reason sits ON the option, not in a legend: the constraint is
          // only useful at the moment of choosing.
          <span className="mt-1 block text-xs text-warning">
            {option.conflict}
          </span>
        ) : null}
      </span>
      <ChevronRight
        className={cn(
          'h-4 w-4 shrink-0 transition-colors',
          clashes
            ? 'text-warning/60'
            : 'text-muted-foreground/50 group-hover:text-primary',
        )}
      />
    </button>
  );
}
